import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaGhost, FaStar, FaTrophy, FaArrowUp, FaCode } from 'react-icons/fa';
import { activityAPI } from '../services/api';
import { useDarkMode } from '../context/DarkModeContext';
import toast from 'react-hot-toast';

const ActivityFeed = ({ type = 'feed', userId = null, limit = 20 }) => {
  const { darkMode } = useDarkMode();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  // Fetch activities (memoized for stable effect deps)
  const fetchActivities = useCallback(async (newOffset = 0) => {
    try {
      setLoading(true);
      let response;

      if (type === 'feed') {
        response = await activityAPI.getFeed({ limit, offset: newOffset });
      } else if (type === 'user' && userId) {
        response = await activityAPI.getUserActivity(userId, { limit, offset: newOffset });
      } else if (type === 'global') {
        response = await activityAPI.getGlobal({ limit, offset: newOffset });
      }

      if (response?.data) {
        if (newOffset === 0) {
          setActivities(response.data.activities || []);
        } else {
          setActivities(prev => [...prev, ...(response.data.activities || [])]);
        }
        setHasMore(response.data.pagination?.hasMore || false);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      toast.error('Failed to load activity feed');
    } finally {
      setLoading(false);
    }
  }, [type, userId, limit]);

  useEffect(() => {
    setOffset(0);
    fetchActivities(0);
  }, [fetchActivities]);

  // Load more activities
  const loadMore = () => {
    const newOffset = offset + limit;
    setOffset(newOffset);
    fetchActivities(newOffset);
  };

  // Get activity icon
  const getActivityIcon = (activityType) => {
    switch (activityType) {
      case 'post_prompt':
        return <FaCode className="text-purple-500" />;
      case 'rate_prompt':
        return <FaStar className="text-yellow-500" />;
      case 'earn_badge':
        return <FaTrophy className="text-gold-500" />;
      case 'level_up':
        return <FaArrowUp className="text-green-500" />;
      case 'fork_prompt':
        return <FaGhost className="text-ghost-white" />;
      default:
        return <FaGhost className="text-gray-400" />;
    }
  };

  // Format activity message
  const formatActivityMessage = (activity) => {
    const username = activity.username || 'Someone';

    switch (activity.activity_type) {
      case 'post_prompt':
        return (
          <span>
            <Link
              to={`/profile/${activity.user_id}`}
              className="font-semibold hover:text-purple-500"
            >
              {username}
            </Link>
            {' '}posted a new prompt{' '}
            {activity.prompt_data && (
              <Link
                to={`/prompt/${activity.prompt_data.id}`}
                className="text-purple-500 hover:text-purple-400"
              >
                "{activity.prompt_data.title}"
              </Link>
            )}
          </span>
        );

      case 'rate_prompt':
        return (
          <span>
            <Link
              to={`/profile/${activity.user_id}`}
              className="font-semibold hover:text-purple-500"
            >
              {username}
            </Link>
            {' '}rated{' '}
            {activity.prompt_data && (
              <Link
                to={`/prompt/${activity.prompt_data.id}`}
                className="text-purple-500 hover:text-purple-400"
              >
                "{activity.prompt_data.title}"
              </Link>
            )}
          </span>
        );

      case 'earn_badge':
        return (
          <span>
            <Link
              to={`/profile/${activity.user_id}`}
              className="font-semibold hover:text-purple-500"
            >
              {username}
            </Link>
            {' '}earned the{' '}
            <span className="text-gold-500 font-semibold">
              {activity.badge_data?.name || 'badge'}
            </span>
            {' '}badge
          </span>
        );

      case 'level_up':
        return (
          <span>
            <Link
              to={`/profile/${activity.user_id}`}
              className="font-semibold hover:text-purple-500"
            >
              {username}
            </Link>
            {' '}leveled up to{' '}
            <span className="text-green-500 font-semibold">
              Level {activity.metadata?.newLevel || activity.level}
            </span>
          </span>
        );

      case 'fork_prompt':
        return (
          <span>
            <Link
              to={`/profile/${activity.user_id}`}
              className="font-semibold hover:text-purple-500"
            >
              {username}
            </Link>
            {' '}forked{' '}
            {activity.prompt_data && (
              <Link
                to={`/prompt/${activity.prompt_data.id}`}
                className="text-purple-500 hover:text-purple-400"
              >
                "{activity.prompt_data.title}"
              </Link>
            )}
          </span>
        );

      default:
        return `${username} performed an action`;
    }
  };

  // Format time ago
  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  if (loading && activities.length === 0) {
    return (
      <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <FaGhost className="text-4xl mx-auto mb-4 animate-pulse" />
        <p>Loading activity...</p>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className={`p-8 text-center ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
        <FaGhost className="text-4xl mx-auto mb-4" />
        <p>No activity yet. Follow some users to see their activity!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {activities.map((activity) => (
        <div
          key={activity.id}
          className={`p-4 rounded-lg border ${
            darkMode
              ? 'bg-dark-surface border-dark-border'
              : 'bg-white border-gray-200'
          } hover:shadow-md transition-shadow`}
        >
          <div className="flex items-start gap-3">
            {/* Activity Icon */}
            <div className="flex-shrink-0 text-2xl mt-1">
              {getActivityIcon(activity.activity_type)}
            </div>

            {/* Activity Content */}
            <div className="flex-1 min-w-0">
              <div className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {formatActivityMessage(activity)}
              </div>

              {/* Activity metadata */}
              <div className="flex items-center gap-4 mt-2">
                <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                  {formatTimeAgo(activity.created_at)}
                </span>

                {activity.points_earned > 0 && (
                  <span className="text-xs text-purple-500 font-medium">
                    +{activity.points_earned} points
                  </span>
                )}

                {/* Badge tier badge */}
                {activity.badge_data && (
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    activity.badge_data.tier === 'gold' ? 'bg-yellow-500 text-yellow-900' :
                    activity.badge_data.tier === 'silver' ? 'bg-gray-300 text-gray-700' :
                    activity.badge_data.tier === 'bronze' ? 'bg-orange-400 text-orange-900' :
                    activity.badge_data.tier === 'ghost' ? 'bg-purple-500 text-white' :
                    'bg-gray-200 text-gray-700'
                  }`}>
                    {activity.badge_data.tier}
                  </span>
                )}
              </div>

              {/* Prompt rating display */}
              {activity.prompt_data && activity.prompt_data.average_rating > 0 && (
                <div className="mt-2 flex items-center gap-2">
                  <FaStar className="text-yellow-500 text-xs" />
                  <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {(parseFloat(activity.prompt_data.average_rating) || 0).toFixed(1)} ({activity.prompt_data.total_ratings} ratings)
                  </span>
                </div>
              )}
            </div>

            {/* User Avatar */}
            {type !== 'user' && activity.avatar_url && (
              <Link to={`/profile/${activity.user_id}`}>
                <img
                  src={activity.avatar_url}
                  alt={activity.username}
                  className="w-10 h-10 rounded-full object-cover"
                />
              </Link>
            )}
          </div>
        </div>
      ))}

      {/* Load More Button */}
      {hasMore && (
        <button
          onClick={loadMore}
          disabled={loading}
          className={`w-full py-3 rounded-lg font-medium transition-colors ${
            darkMode
              ? 'bg-dark-surface text-ghost-white border border-dark-border hover:bg-dark-hover'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {loading ? 'Loading...' : 'Load More'}
        </button>
      )}
    </div>
  );
};

export default ActivityFeed;
